import { omitBy } from 'lodash';
import { type StateCreator } from 'zustand';

import { revokeBlobObjectUrls } from '../../smartResizeRetry/utils/revokeBlobObjectUrls';

export type GroupCompositeThumbnailSlice = {
  clearPending: (groupId: string) => void;
  clearThumbnails: () => void;
  markPending: (groupId: string) => void;
  pendingGroupIds: Record<string, true>;
  signatures: Record<string, string>;
  thumbnails: Record<string, string>;
  removeStaleThumbnails: (activeGroupIds: Set<string>) => void;
  removeThumbnailsForGroupIds: (groupIds: Set<string>) => void;
  setThumbnail: (groupId: string, signature: string, url: string) => void;
};

export type CreateGroupCompositeThumbnailSlice = () => StateCreator<
  GroupCompositeThumbnailSlice,
  [],
  [],
  GroupCompositeThumbnailSlice
>;

export const createGroupCompositeThumbnailSlice: CreateGroupCompositeThumbnailSlice = () => (set, get) => ({
  clearPending: (groupId) => {
    const { pendingGroupIds } = get();

    if (!(groupId in pendingGroupIds)) {
      return;
    }

    set({ pendingGroupIds: omitBy(pendingGroupIds, (_value, key) => key === groupId) });
  },

  clearThumbnails: () => {
    const { thumbnails } = get();

    revokeBlobObjectUrls(Object.values(thumbnails));

    set({ pendingGroupIds: {}, signatures: {}, thumbnails: {} });
  },

  markPending: (groupId) => {
    const { pendingGroupIds } = get();

    if (pendingGroupIds[groupId]) {
      return;
    }

    set({ pendingGroupIds: { ...pendingGroupIds, [groupId]: true } });
  },

  pendingGroupIds: {},

  removeStaleThumbnails: (activeGroupIds) => {
    const { pendingGroupIds, signatures, thumbnails } = get();
    const nextPending = { ...pendingGroupIds };
    const nextSignatures = { ...signatures };
    const nextThumbnails = { ...thumbnails };
    let changed = false;

    for (const groupId of Object.keys(nextThumbnails)) {
      if (!activeGroupIds.has(groupId)) {
        revokeBlobObjectUrls([nextThumbnails[groupId] ?? '']);
        delete nextSignatures[groupId];
        delete nextThumbnails[groupId];
        changed = true;
      }
    }

    for (const groupId of Object.keys(nextPending)) {
      if (!activeGroupIds.has(groupId)) {
        delete nextPending[groupId];
        changed = true;
      }
    }

    if (changed) {
      set({ pendingGroupIds: nextPending, signatures: nextSignatures, thumbnails: nextThumbnails });
    }
  },

  removeThumbnailsForGroupIds: (groupIds) => {
    if (groupIds.size === 0) {
      return;
    }

    const { pendingGroupIds, signatures, thumbnails } = get();
    const nextPending = { ...pendingGroupIds };
    const nextSignatures = { ...signatures };
    const nextThumbnails = { ...thumbnails };
    let changed = false;

    for (const groupId of groupIds) {
      if (groupId in nextPending) {
        delete nextPending[groupId];
        changed = true;
      }

      if (!(groupId in nextThumbnails)) {
        continue;
      }

      revokeBlobObjectUrls([nextThumbnails[groupId] ?? '']);
      delete nextSignatures[groupId];
      delete nextThumbnails[groupId];
      changed = true;
    }

    if (changed) {
      set({ pendingGroupIds: nextPending, signatures: nextSignatures, thumbnails: nextThumbnails });
    }
  },

  setThumbnail: (groupId, signature, url) => {
    const { pendingGroupIds, signatures, thumbnails } = get();
    const previousUrl = thumbnails[groupId];
    const nextPending =
      groupId in pendingGroupIds ? omitBy(pendingGroupIds, (_value, key) => key === groupId) : pendingGroupIds;

    if (signatures[groupId] === signature && previousUrl === url) {
      if (nextPending !== pendingGroupIds) {
        set({ pendingGroupIds: nextPending });
      }

      return;
    }

    if (previousUrl && previousUrl !== url) {
      revokeBlobObjectUrls([previousUrl]);
    }

    set({
      pendingGroupIds: nextPending,
      signatures: { ...signatures, [groupId]: signature },
      thumbnails: { ...thumbnails, [groupId]: url },
    });
  },
  signatures: {},
  thumbnails: {},
});
